import { ApartmentOutlined, CheckCircleFilled, DownOutlined, SearchOutlined, ShopOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Breadcrumb, Button, Flex, Input, List, Radio, Skeleton, Space, Tag, Typography, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/api/services';
import { useSessionStore } from '@/stores/session-store';
import type { OrganizationNode, Seller } from '@/types/api';
import { overlaySizes } from '@/theme/theme';
import { StableModal } from '@/components/overlay/StableModal';
import { getUserFacingErrorMessage } from '@/components/feedback/errorMessages';
import { getNavigationItem, isNavigationItemAvailable } from '@/config/navigation';
import { useOrganizationContextSwitch } from './useOrganizationContextSwitch';

const typeColors: Record<OrganizationNode['organizationType'], string> = {
  system_provider: 'geekblue', enterprise: 'purple', brand: 'cyan', store: 'green',
};
const typeNames: Record<OrganizationNode['organizationType'], string> = {
  system_provider: '系統商', enterprise: '企業', brand: '品牌', store: '門市',
};
const EMPTY_ORGANIZATIONS: OrganizationNode[] = [];
const EMPTY_SELLERS: Seller[] = [];

export function ContextSwitcher() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<OrganizationNode | null>(null);
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(null);
  const me = useSessionStore((state) => state.me);
  const contextVersion = me?.context.contextVersion ?? 'anonymous';
  const organizationsQuery = useQuery({
    queryKey: ['organization-contexts', contextVersion, keyword],
    queryFn: ({ signal }) => api.organizations(keyword, signal),
    enabled: open,
  });
  const sellersQuery = useQuery({
    queryKey: ['organization-sellers', contextVersion, selected?.id],
    queryFn: ({ signal }) => api.sellers(selected!.id, signal),
    enabled: open && Boolean(selected?.id),
  });
  const organizations = organizationsQuery.data ?? EMPTY_ORGANIZATIONS;
  const sellers = sellersQuery.data ?? EMPTY_SELLERS;
  const switchMutation = useOrganizationContextSwitch();

  useEffect(() => {
    if (!selected) {
      setSelectedSellerId(null);
      return;
    }
    if (sellers.length === 1) setSelectedSellerId(sellers[0]!.id);
    else setSelectedSellerId(null);
  }, [selected, sellers]);

  const close = () => {
    setOpen(false);
    setSelected(null);
    setSelectedSellerId(null);
    setKeyword('');
  };

  const switchContext = async () => {
    if (!selected) return;
    try {
      const nextMe = await switchMutation.mutateAsync({ organizationId: selected.id, sellerId: selectedSellerId });
      message.success(t('context.switched'));
      close();
      const currentItem = getNavigationItem(location.pathname);
      if (currentItem && !isNavigationItemAvailable(currentItem, nextMe)) navigate('/dashboard', { replace: true });
    } catch {
      // 保留目前選擇，讓使用者查看原因後直接重試。
    }
  };

  const currentPath = useMemo(() => me?.context.organization.path.map((item) => ({ title: item.name })) ?? [], [me]);
  const contextDetail = useMemo(() => {
    const organization = me?.context.organization;
    if (!organization || organization.organizationType !== 'store') return me?.context.seller?.displayName ?? '';
    const brand = organization.path.find((item) => item.organizationType === 'brand');
    if (brand) return `${t('context.brandStore')} · ${brand.name}`;
    const enterprise = organization.path.find((item) => item.organizationType === 'enterprise');
    return enterprise ? `${t('context.enterpriseDirect')} · ${enterprise.name}` : t('context.enterpriseDirect');
  }, [me, t]);
  const sellerSelectionRequired = sellers.length > 1;
  const confirmDisabled = !selected?.canEnter
    || sellersQuery.isLoading
    || sellersQuery.isError
    || (sellerSelectionRequired && !selectedSellerId);

  if (!me) return <Skeleton.Button active size="small" />;

  return (
    <>
      <Button className="context-button" onClick={() => setOpen(true)} aria-label={t('context.switch')}>
        <Flex align="center" gap={10}>
          <span className="context-icon"><ShopOutlined /></span>
          <span className="context-copy">
            <span className="context-label">{t('context.current')}</span>
            <strong>{me.context.organization.name}</strong>
            {contextDetail && <small>{contextDetail}</small>}
          </span>
          <DownOutlined />
        </Flex>
      </Button>
      <StableModal
        title={<Space><ApartmentOutlined />{t('context.switch')}</Space>}
        open={open}
        onCancel={close}
        width={overlaySizes.selectionModal}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: confirmDisabled, loading: switchMutation.isPending }}
        onOk={() => void switchContext()}
      >
        <Alert type="info" showIcon message={t('context.warning')} className="context-alert" />
        {organizationsQuery.isError && <Alert type="error" showIcon message={t('context.organizationsLoadFailed')} description={getUserFacingErrorMessage(organizationsQuery.error)} className="context-alert" />}
        {switchMutation.isError && <Alert type="error" showIcon message={t('context.switchFailed')} description={getUserFacingErrorMessage(switchMutation.error)} className="context-alert" />}
        <Typography.Text type="secondary">{t('context.currentPath')}</Typography.Text>
        <Breadcrumb items={currentPath} className="context-breadcrumb" />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('context.searchPlaceholder')}
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          className="context-search"
        />
        <List
          loading={organizationsQuery.isLoading}
          dataSource={organizations}
          locale={{ emptyText: t('common.noData') }}
          className="context-list"
          renderItem={(item) => (
            <List.Item className={`context-list-item ${selected?.id === item.id ? 'selected' : ''} ${!item.canEnter ? 'disabled' : ''}`}>
              <button
                type="button"
                className="context-list-button"
                disabled={!item.canEnter}
                aria-pressed={selected?.id === item.id}
                onClick={() => setSelected(item)}
              >
                <span className="organization-avatar" aria-hidden="true">{item.depth}</span>
                <span className="context-organization-copy">
                  <span className="context-organization-title"><strong>{item.name}</strong><Tag color={typeColors[item.organizationType]}>{typeNames[item.organizationType]}</Tag></span>
                  <span className="context-organization-meta">{item.code}{item.unavailableReason && <Typography.Text type="danger"> · {item.unavailableReason}</Typography.Text>}</span>
                </span>
                {selected?.id === item.id && <CheckCircleFilled className="selected-check" aria-hidden="true" />}
              </button>
            </List.Item>
          )}
        />
        {selected && (
          <section className="seller-selection" aria-labelledby="seller-selection-title">
            <Typography.Text strong id="seller-selection-title">{t('context.seller')}</Typography.Text>
            {sellersQuery.isLoading && <Skeleton active paragraph={{ rows: 1 }} title={false} />}
            {sellersQuery.isError && <Alert type="error" showIcon message={t('context.sellersLoadFailed')} description={getUserFacingErrorMessage(sellersQuery.error)} />}
            {!sellersQuery.isLoading && !sellersQuery.isError && sellers.length === 0 && <Typography.Text type="secondary">{t('context.noSellerRequired')}</Typography.Text>}
            {!sellersQuery.isLoading && !sellersQuery.isError && sellers.length === 1 && <Typography.Text>{sellers[0]!.displayName}（{sellers[0]!.taxIdMasked}）</Typography.Text>}
            {!sellersQuery.isLoading && !sellersQuery.isError && sellers.length > 1 && (
              <>
                <Typography.Text type="secondary">{t('context.sellerSelectionRequired')}</Typography.Text>
                <Radio.Group value={selectedSellerId} onChange={(event) => setSelectedSellerId(event.target.value)} className="seller-radio-group">
                  {sellers.map((seller) => <Radio key={seller.id} value={seller.id}>{seller.displayName}（{seller.taxIdMasked}）</Radio>)}
                </Radio.Group>
              </>
            )}
          </section>
        )}
      </StableModal>
    </>
  );
}
